import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import { useOptions } from '../../stateManagers/hooks/useOptions';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface StudentData {
  id: number;
  user_id: number;
  name: string;
  email_id: string;
  class_id: string;
  class_name: string;
  section: string | null;
  roll_no: number | string;
  father_name: string;
  father_no: string;
  profile_picture_url: string | null;
}

interface ExamItem {
  id: number;
  subject_name: string;
  exam_date: string;
  session: string;
}

interface DateSheetData {
  session: any;
  class_id: number;
  class_name: string;
  exam_type_name: string;
  title: string;
  total_items: number;
  items: ExamItem[];
}

const HallTicketGenerator: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [dateSheet, setDateSheet] = useState<DateSheetData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const { options: schoolOptions } = useOptions<string[]>(['school_name', 'current_session']);
  const schoolName = (schoolOptions['school_name']?.data?.[0] ?? 'School') as string;

  useEffect(() => {
    if (selectedClass && shouldFetchStudents()) {
      fetchStudents();
      fetchDateSheet();
    } else {
      setStudents([]);
      setDateSheet(null);
      console.log()
    }
  }, [selectedClass, selectedSection]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => {
    setClasses(loadedClasses);
  };

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classes.find(c => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const shouldFetchStudents = (): boolean => {
    if (!selectedClass) return false;
    const hasSections = selectedClassHasSections();
    if (hasSections) {
      return selectedSection !== null;
    }
    return true;
  };

  const fetchStudents = async (): Promise<void> => {
    if (!selectedClass || !shouldFetchStudents()) {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      let params: any = {
        page: 1,
        limit: 100,
        class_id: selectedClass
      };

      if (selectedClassHasSections() && selectedSection) {
        params.section = selectedSection;
      }

      const response = await apiClient.get('/get-students-list/class-section', {
        params
      });

      const data = response.data;

      if (data.status === 'success') {
        setStudents(data.data);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch students',
          life: 4000
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch students:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching students',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDateSheet = async (): Promise<void> => {
  if (!selectedClass || !shouldFetchStudents()) {
    setDateSheet(null);
    return;
  }

  try {
    let params: any = {
      class_id: selectedClass
    };

    if (selectedClassHasSections() && selectedSection) {
      params.section = selectedSection;
    }

    const response = await apiClient.get('/student-exam-date-sheet', {
      params
    });

    const data = response.data;

    if (data.status === 'success') {
      setDateSheet(data.data);
      console.log("DateSheet Data:", data.data); // Add this line
    } else {
      setDateSheet(null);
      toast.current?.show({
        severity: 'warn',
        summary: 'No Exam Schedule',
        detail: 'No exam date sheet found for selected class/section',
        life: 4000
      });
    }
  } catch (err: any) {
    console.error('Failed to fetch date sheet:', err);
    setDateSheet(null);
  }
};

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
  };

  const formatDateWithDay = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const createStudentHallTicket = (student: StudentData, className: string, sectionText: string) => {
    const isLandscape = orientation === 'landscape';

    return `
      <div class="student-info">
        <div class="student-photo">
          ${student.profile_picture_url
            ? `<img src="${student.profile_picture_url}" alt="${student.name}" class="profile-image" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
               <div class="photo-placeholder" style="display: none;">${student.name.charAt(0)}</div>`
            : `<div class="photo-placeholder">${student.name.charAt(0)}</div>`
          }
        </div>
        <div class="student-details">
          <table class="details-table">
            <tr><td class="label">Student Name:</td><td class="value">${student.name}</td></tr>
            <tr><td class="label">Roll Number:</td><td class="value">${student.roll_no}</td></tr>
            <tr><td class="label">Class:</td><td class="value">${className}${sectionText}</td></tr>
            <tr><td class="label">Father's Name:</td><td class="value">${student.father_name}</td></tr>
          </table>
        </div>
      </div>

      <div class="exam-schedule">
        <table class="schedule-table">
          <thead>
            <tr><th>Date & Day</th><th>Subject</th></tr>
          </thead>
          <tbody>
            ${dateSheet!.items.map(exam => `
              <tr><td>${formatDateWithDay(exam.exam_date)}</td><td>${exam.subject_name}</td></tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="instructions">
        <p class="instructions-text">INSTRUCTIONS FOR CANDIDATES</p>
        <ul class="instructions-list">
          <li>Report to the examination center 30 minutes before the scheduled time</li>
          <li>Bring this admit card along with a valid photo ID</li>
          <li>Mobile phones and electronic devices are strictly prohibited</li>
          <li>Use only blue or black pen for writing</li>
          <li>Do not leave the examination hall without permission</li>
        </ul>
      </div>

      <div class="signatures">
        <div class="signature-box">
          <div class="signature-line"></div>
          <p class="signature-label">Form Teacher's Signature</p>
        </div>
        <div class="signature-box">
          <div class="signature-line"></div>
          <p class="signature-label">Principal's Signature</p>
        </div>
      </div>
    `;
  };

  const getStyles = () => {
    const isLandscape = orientation === 'landscape';
    const scale = 1;

    return `
      @page {
        size: A4 ${orientation};
        margin: 0.5in;
      }

      @media print {
        body { margin: 0; font-family: Arial, sans-serif; color: #1e40af; background: white; }
        .no-print { display: none !important; }
        .hall-ticket-page { page-break-after: always; page-break-inside: avoid; }
      }

      body {
        font-family: Arial, sans-serif;
        margin: 8px;
        padding: 10px;
        background: white;
        color: #1e40af;
        font-size: ${12 * scale}px;
        line-height: 1.3;
      }

      .controls {
        margin-bottom: 15px;
        text-align: center;
      }

      .controls button {
        background: #1e40af;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        margin: 0 5px;
      }

      .controls button.close-btn {
        background: #dc2626;
      }

      .hall-ticket-page {
        width: 100%;
        max-width: ${isLandscape ? 1000 : 800}px;
        margin: 0 auto 20px auto;
        border: 2px solid #1e40af;
        border-radius: 8px;
        background: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .ticket-header {
        background: white;
        color: #1e40af;
        padding: ${15 * scale}px;
        text-align: center;
        border-radius: 6px 6px 0 0;
        border-bottom: 2px solid #1e40af;
      }

      .school-name {
        margin: 0 0 8px 0;
        font-size: ${(isLandscape ? 28 : 20) * scale}px;
        font-weight: bold;
        letter-spacing: 1px;
      }

      .exam-title {
        margin: 0 0 4px 0;
        font-size: ${(isLandscape ? 20 : 16) * scale}px;
        font-weight: 600;
      }

      .admit-card-title {
        margin: 0;
        font-size: ${(isLandscape ? 16 : 14) * scale}px;
        font-weight: 500;
      }

      .ticket-body { padding: ${20 * scale}px; }

      .student-info {
        display: flex;
        gap: ${20 * scale}px;
        margin-bottom: ${20 * scale}px;
        align-items: flex-start;
      }

      .student-photo { flex-shrink: 0; }

      .profile-image, .photo-placeholder {
        width: ${(isLandscape ? 140 : 100) * scale}px;
        height: ${(isLandscape ? 160 : 120) * scale}px;
        border: 2px solid #1e40af;
        border-radius: 6px;
      }

      .profile-image { object-fit: cover; }

      .photo-placeholder {
        background: #f8fafc;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${(isLandscape ? 60 : 40) * scale}px;
        font-weight: bold;
        color: #1e40af;
      }

      .student-details { flex: 1; }

      .details-table {
        width: 100%;
        border-collapse: collapse;
      }

      .details-table td {
        padding: ${8 * scale}px ${12 * scale}px;
        border-bottom: 1px solid #e5e7eb;
        font-size: ${14.4 * scale}px;
      }

      .details-table .label {
        font-weight: 1200;
        width: ${140 * scale}px;
      }

      .details-table .value { font-weight: 1000; }

      .exam-schedule { margin-bottom: ${20 * scale}px; }

      .schedule-table {
        width: 100%;
        border-collapse: collapse;
        border: 2px solid #1e40af;
      }

      .schedule-table th {
        background: white;
        padding: ${12 * scale}px ${8 * scale}px;
        text-align: center;
        font-weight: 1200;
        border: 1px solid #1e40af;
        font-size: ${(isLandscape ? 19.2 : 14.4) * scale}px;
      }

      .schedule-table td {
        padding: ${10 * scale}px ${8 * scale}px;
        border: 1px solid #1e40af;
        text-align: center;
        font-size: ${(isLandscape ? 16.8 : 13.2) * scale}px;
        font-weight: 1000;
      }

      .schedule-table tr:nth-child(even) { background: #f8fafc; }

      .instructions { margin-bottom: ${20 * scale}px; }

      .instructions-text {
        font-weight: 1600;
        margin-bottom: ${8 * scale}px;
        font-size: ${(isLandscape ? 16.8 : 14.4) * scale}px;
      }

      .instructions-list {
        margin: 0;
        padding-left: ${20 * scale}px;
        font-size: ${(isLandscape ? 14.4 : 12) * scale}px;
        font-weight: 1000;
      }

      .instructions-list li {
        margin-bottom: ${4 * scale}px;
        line-height: 1.4;
      }

      .signatures {
        display: flex;
        justify-content: space-between;
        margin-top: ${20 * scale}px;
      }

      .signature-box {
        text-align: center;
        width: ${160 * scale}px;
      }

      .signature-line {
        height: 1px;
        background: #1e40af;
        margin-bottom: ${4 * scale}px;
      }

      .signature-label {
        margin: 0;
        font-size: ${12 * scale}px;
        font-weight: 500;
      }

      .ticket-footer {
        text-align: center;
        padding: 6px;
        font-size: 9px;
        color: #94a3b8;
        border-top: 1px solid #e5e7eb;
        letter-spacing: 0.05em;
      }
    `;
  };

  const generateHallTickets = () => {
    if (students.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Students',
        detail: 'No students available to generate hall tickets',
        life: 4000
      });
      return;
    }

    if (!dateSheet || dateSheet.items.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Exam Schedule',
        detail: 'No exam date sheet available for selected class/section',
        life: 4000
      });
      return;
    }

    const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
    const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
    const title = `Hall Tickets - Class ${className}${sectionText}`;

    const hallTicketsContent = students.map(student => `
      <div class="hall-ticket-page">
        <div class="ticket-header">
          <div class="school-info">
            <h1 class="school-name">${schoolName}</h1>
            <h2 class="exam-title">${dateSheet.title} EXAMINATION - ${dateSheet.session}</h2>
            <h3 class="admit-card-title">ADMIT CARD / HALL TICKET</h3>
          </div>
        </div>
        <div class="ticket-body">
          ${createStudentHallTicket(student, className, sectionText)}
        </div>
        <div class="ticket-footer">Powered by SkoolPillar</div>
      </div>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>${getStyles()}</style>
        </head>
        <body>
          <div class="no-print controls">
            <button onclick="window.print()">🖨️ Print / Save as PDF</button>
            <button class="close-btn" onclick="window.close()">❌ Close</button>
          </div>
          ${hallTicketsContent}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Generated hall tickets for ${students.length} students`,
        life: 4000
      });
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to open print window. Please check your browser settings.',
        life: 5000
      });
    }
  };

  const orientationOptions = [
    { label: '📄 Portrait', value: 'portrait' },
    { label: '📃 Landscape', value: 'landscape' }
  ];

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        {/* Header */}
        <GlobalHeaderTitle
          title="Hall Ticket Generator"
          subtitle="Generate printable hall tickets/admit cards with exam schedule"
          icon="pi pi-id-card"
          buttons={[
            {
              label: "Generate Hall Tickets",
              icon: "pi pi-file-pdf",
              onClick: generateHallTickets,
              disabled: students.length === 0 || !dateSheet || loading,
              style: {
                background: students.length > 0 && dateSheet && !loading
                  ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                  : undefined
              }
            }
          ]}
        />

        {/* Class Section Filter */}
        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
        />

        {/* Orientation Settings */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#1e40af',
                marginBottom: '8px',
                letterSpacing: '0.02em'
              }}>
                Page Orientation
              </label>
              <Dropdown
                value={orientation}
                options={orientationOptions}
                onChange={(e) => setOrientation(e.value)}
                placeholder="Select Orientation"
                style={{
                  width: '100%',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Students and Exam Info Preview */}
        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            width: '100%'
          }}>
            {students.length > 0 && dateSheet ? (
              <>
                {/* Info Bar */}
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="pi pi-users" style={{ fontSize: '14px', color: '#059669' }}></i>
                      </div>
                      <span style={{
                        fontSize: '13px',
                        color: '#64748b',
                        fontWeight: '600'
                      }}>
                        {students.length} student{students.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.08) 100%)',
                      border: '1px solid rgba(245, 158, 11, 0.15)',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: '#f59e0b',
                        fontWeight: '600'
                      }}>
                        Exam: {dateSheet.title}
                      </span>
                    </div>

                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.08) 100%)',
                      border: '1px solid rgba(99, 102, 241, 0.15)',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: '#6366f1',
                        fontWeight: '600'
                      }}>
                        Subjects: {dateSheet.total_items}
                      </span>
                    </div>

                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(244, 114, 182, 0.08) 100%)',
                      border: '1px solid rgba(236, 72, 153, 0.15)',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: '#ec4899',
                        fontWeight: '600'
                      }}>
                        {students.length} sheet{students.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exam Schedule Preview */}
                <div style={{ padding: '20px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e40af',
                    marginBottom: '12px'
                  }}>
                    Exam Schedule
                  </h3>
                  <div style={{
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#1e40af',
                            borderBottom: '1.5px solid #e5e7eb'
                          }}>
                            Date & Day
                          </th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#1e40af',
                            borderBottom: '1.5px solid #e5e7eb'
                          }}>
                            Subject
                          </th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#1e40af',
                            borderBottom: '1.5px solid #e5e7eb'
                          }}>
                            Session
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dateSheet.items.map((exam, index) => (
                          <tr key={exam.id} style={{
                            background: index % 2 === 0 ? 'white' : '#fafbfc'
                          }}>
                            <td style={{
                              padding: '10px 12px',
                              fontSize: '13px',
                              color: '#1f2937',
                              borderBottom: '1px solid #f3f4f6'
                            }}>
                              {formatDateWithDay(exam.exam_date)}
                            </td>
                            <td style={{
                              padding: '10px 12px',
                              fontSize: '13px',
                              color: '#1f2937',
                              fontWeight: '500',
                              borderBottom: '1px solid #f3f4f6'
                            }}>
                              {exam.subject_name}
                            </td>
                            <td style={{
                              padding: '10px 12px',
                              fontSize: '13px',
                              color: '#6b7280',
                              borderBottom: '1px solid #f3f4f6'
                            }}>
                              {exam.session}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Students Grid Preview */}
                <div style={{
                  padding: '0 20px 20px 20px',
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e40af',
                    marginBottom: '12px'
                  }}>
                    Students ({students.length})
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '16px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {students.map((student) => (
                      <div
                        key={student.id}
                        style={{
                          border: '1.5px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '16px',
                          background: '#fafbfc',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          {student.profile_picture_url ? (
                            <img
                              src={`${student.profile_picture_url}`}
                              alt={student.name}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '10px',
                                objectFit: 'cover',
                                border: '2px solid #1e40af'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '10px',
                              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '18px',
                              fontWeight: '600',
                              color: 'white'
                            }}>
                              {student.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: '600',
                              fontSize: '16px',
                              color: '#1f2937',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {student.name}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: '#6b7280'
                            }}>
                              Roll: {student.roll_no}
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          lineHeight: 1.6
                        }}>
                          <div><strong>Class:</strong> {student.class_name}{student.section ? ` - ${student.section}` : ''}</div>
                          <div><strong>Father:</strong> {student.father_name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                minHeight: '400px'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                }}>
                  <i
                    className={
                      !selectedClass
                        ? "pi pi-filter"
                        : selectedClassHasSections() && !selectedSection
                        ? "pi pi-list"
                        : students.length === 0
                        ? "pi pi-inbox"
                        : "pi pi-calendar-times"
                    }
                    style={{ fontSize: '25px', color: 'white' }}
                  ></i>
                </div>
                <h3 style={{
                  margin: 0,
                  color: '#1e40af',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  {!selectedClass
                    ? "No Class Selected"
                    : selectedClassHasSections() && !selectedSection
                    ? "No Section Selected"
                    : students.length === 0
                    ? "No Students Found"
                    : "No Exam Schedule Available"
                  }
                </h3>
                <p style={{
                  margin: 0,
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'center',
                  maxWidth: '400px',
                  lineHeight: 1.6
                }}>
                  {!selectedClass
                    ? "Please select a class from the filter above to load students and exam schedule"
                    : selectedClassHasSections() && !selectedSection
                    ? "Please select a section to load students and exam schedule for this class"
                    : students.length === 0
                    ? "There are no students enrolled in this class/section yet"
                    : "No exam date sheet found for the selected class/section. Please create an exam schedule first."
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HallTicketGenerator;
